/**
 * @fileoverview Capability matrix query hook.
 * @description Wraps the role/permission matrix read. Always refetches when the page opens
 * (staleTime 0) so a permission added to the tables since the last visit shows up straight away.
 * While an edit is open the caller pauses it, so a window refocus or a reconnect doesn't pull
 * someone else's changes in under the admin's draft; the read is made again on its own after a
 * save, and on the admin's request.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useQuery } from '@tanstack/react-query'

import { CAPABILITIES_QUERY_KEYS } from '@/features/capabilities/constants/capabilities-query-keys.constants'
import { getCapabilityMatrix } from '@/features/capabilities/services/role-capabilities.service'

import type { CapabilityMatrix } from '@/features/capabilities/types/capabilities.types'
import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Fetches the role/permission matrix.
 * @param {boolean} [paused] - True while an edit is open: no refetch on window focus or reconnect.
 * @returns {import('@tanstack/react-query').UseQueryResult<CapabilityMatrix, ApiError>} The query
 * result.
 */
export function useCapabilityMatrix(paused = false) {
  return useQuery<CapabilityMatrix, ApiError>({
    queryKey: CAPABILITIES_QUERY_KEYS.matrix,
    queryFn: getCapabilityMatrix,
    staleTime: 0,
    refetchOnWindowFocus: !paused,
    refetchOnReconnect: !paused,
  })
}
